/**
 * @module usePhoneNumberValidation
 * @description Custom hook for validating and formatting phone numbers based on country code
 */

// import { useDatabridge } from '@xp-utilities/web';
import jsCookie from 'js-cookie';
import parsePhoneNumber, { AsYouType, getCountryCallingCode } from 'libphonenumber-js';

/*
  TODO:
  Using the uPref cookie to get country is a temporary solution.
  We want to get the country from the databridge, however, there is a caching issue
  with responsiveNav.jsp for certian segmentations. See DEP-1152.
  There is an MR to resolve this issue, but it is not yet merged:
  https://git.homeoffice.anfcorp.com/ecommerce/crs-app/-/merge_requests/6274
  Leaving the code retrieving country from databridge commented out for now.
  */

/**
 * @function usePhoneNumberValidation
 * @description Hook that provides phone number validation and formatting based on country
 *
 * @returns {Function} validate - Function to validate and format phone numbers
 *
 * @example
 * const validate = usePhoneNumberValidation();
 *
 * // In your component:
 * const { isValid, formattedPhoneNumber } = validate(phoneInput);
 *
 * // Display formatted number and validation message
 * <input
 *   value={formattedPhoneNumber}
 *   onChange={(e) => setPhoneInput(e.target.value)}
 *   className={!isValid ? 'invalid' : ''}
 * />
 * {!isValid && <span className="error">Please enter a valid phone number</span>}
 */
export default function usePhoneNumberValidation() {
  // const [getCountry] = useDatabridge('page.country', 'US');

  /**
   * @function validate
   * @description Validates and formats a phone number based on country code
   *
   * @param {string} phoneNumber - The phone number to validate
   * @returns {Object} Result object containing:
   *   - {boolean} isValid - Whether the phone number is valid
   *   - {string} formattedPhoneNumber - The formatted phone number
   */
  const validate = (phoneNumber) => {
    // const country = getCountry() || 'US';
    const uPref = jsCookie.get('uPref');
    const uPrefObj = uPref ? JSON.parse(uPref) : {};
    // defaulting to US because county coud me missing from uPref cookie
    const countryFromUPref = uPrefObj?.shipTo || 'US';

    // get the numerical country calling code for the given country code
    const countryCallingCode = getCountryCallingCode(countryFromUPref);

    if (!phoneNumber) return { isValid: false, formattedPhoneNumber: `+${countryCallingCode} ` };

    /*
      currently validating using isPossbile() method from libphonenumber-js
      which checks  if the number is possible but not necessarily valid (e.g. length check)
      alternativley we could use isValid() to check if the actual phone number digits AND length
      are valid for the given country
    */
    const parsedPhoneNumber = parsePhoneNumber(phoneNumber, countryFromUPref);
    const isPossiblePhoneNumber = parsedPhoneNumber?.isPossible() || false;

    // format phone number as user types
    const formattedPhoneNumber = new AsYouType(countryFromUPref)?.input(phoneNumber);

    return {
      isValid: isPossiblePhoneNumber,
      formattedPhoneNumber,
    };
  };

  return validate;
}
